/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import Head from 'next/head';
import { mapCitrusBannerToContentStackBanner } from '@/features/citrus/utils/mapCitrusBannerToContentStackBanner';
import {
  useCitrusSearchPageData,
  useCitrusShelfData,
} from '@/features/citrus/api/getCitrusShelfData';
import { useFeatureFlag } from '@/features/globalConfig';
import { isCitrusBannerWithLink } from '@/features/citrus/utils/isCitrusBannerWithLink';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import type { ContentStackProductListingPage } from '@/features/contentstack/interface';
import { extractOrigin } from '@/features/contentstack/utils/extractOrigin';
import { CMSShelfSpacer } from '@/features/contentstack/components/CMSShelfSpacer/CMSShelfSpacer';
import { CMSBanner } from '@/features/contentstack/components/CMSBanner/CMSBanner';
import { useLabels } from '@/hooks/useLabels';

type Props = {
  content: Extract<
    NonNullable<ContentStackProductListingPage['shelfpagebody']>[number],
    { ad: unknown }
  >['ad'];
};

const labelMap = {
  clickForDetails: 'label_click_for_details',
} as const;

const sizePerDevice = {
  desktop: {
    width: 1166,
    height: 110,
  },
  mobile: {
    width: 668,
    height: 250,
  },
  tablet: {
    width: 1100,
    height: 115,
  },
};

export const TopShelfAd = ({ content }: Props) => {
  const labels = useLabels(labelMap);
  const citrusShelfBannersEnabled = useFeatureFlag('IS_CITRUS_SHELF_BANNERS_ENABLED') === 'true';
  const citrusSearchBannersEnabled =
    useFeatureFlag('IS_CITRUS_SEARCH_RESULTS_BANNERS_ENABLED') === 'true';

  const router = useLegacyRouter();
  const isSearchPage = router.pathname.includes('/searchresult');
  const { data: citrusShelfData } = useCitrusShelfData({ enabled: citrusShelfBannersEnabled });
  const { data: citrusSearchData } = useCitrusSearchPageData({
    enabled: citrusSearchBannersEnabled,
  });

  const searchBannersToDisplay = citrusSearchData?.ads?.find(
    (adSlot) => adSlot?.slotId === content?.citrus_ad?.[0].slot_id,
  )?.banners;
  const shelfBannersToDisplay = citrusShelfData?.ads?.find(
    (adSlot) => adSlot?.slotId === content?.citrus_ad?.[0].slot_id,
  )?.banners;

  const bannersToDisplay = isSearchPage ? searchBannersToDisplay : shelfBannersToDisplay;

  const citrusImgOrigin = !!bannersToDisplay && extractOrigin(bannersToDisplay[0]?.backgroundImage);

  const hasBannersToDisplay = !!bannersToDisplay?.length;

  return (
    <>
      {citrusImgOrigin ? (
        <Head>
          <link key="citrus-preconnect" rel="preconnect" href={citrusImgOrigin} />
        </Head>
      ) : null}
      {hasBannersToDisplay &&
        bannersToDisplay?.map((item, i) =>
          isCitrusBannerWithLink(item) ? (
            <CMSShelfSpacer
              key={i}
              removeHorizontalSpacing={{ s: true, l: true }}
              {...(mapCitrusBannerToContentStackBanner(item, labels.clickForDetails)
                .component_id && {
                id: mapCitrusBannerToContentStackBanner(item, labels.clickForDetails).component_id,
              })}
            >
              <CMSBanner
                key={i}
                content={mapCitrusBannerToContentStackBanner(item, labels.clickForDetails)}
                sponsored
                citrusInfo={{ citrusAdId: item.citrusAdId }}
                legalPadding={{ s: true, m: true, l: false }}
                priority
                sizePerDevice={sizePerDevice}
              />
            </CMSShelfSpacer>
          ) : null,
        )}
      {!hasBannersToDisplay && content?.az_banner?.[0] && (
        <>
          <CMSShelfSpacer
            key={'az_banner'}
            removeHorizontalSpacing={{ s: true, l: true }}
            {...(content?.az_banner?.[0].component_id && {
              id: content?.az_banner?.[0].component_id,
            })}
          >
            <CMSBanner
              key={'az_banner'}
              content={content?.az_banner?.[0]}
              legalPadding={{ s: true, m: true, l: false }}
              priority
              sizePerDevice={sizePerDevice}
            />
          </CMSShelfSpacer>
        </>
      )}
    </>
  );
};
